import React, { useEffect, useState } from "react";
import { Button } from "@heroui/react";
import { ArrowLeftIcon, ChevronRightIcon, DocumentIcon, XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { OpportunityCardTemplate, OpportunityCardTemplateJson } from "../../../types/types";
import OpportunityFormContainer from "./opportunityFormContainer";
import type { EventOpportunityDefaults } from "./opportunityForm";
import eventIcon from "../../../assets/opportunityPages/contractProgress/eventIcon.svg";
import fundsIcon from "../../../assets/opportunityPages/contractProgress/fundsIcon.svg";
import payoutIcon from "../../../assets/opportunityPages/contractProgress/payoutIcon.svg";
import resultIcon from "../../../assets/opportunityPages/contractProgress/resultIcon.svg";
import signatureIcon from "../../../assets/opportunityPages/contractProgress/signatureIcon.svg";

// Icon ids come from contractProgress[].icon in the template JSON.
const CONTRACT_PROGRESS_ICONS: Record<string, string> = {
  document_sign: signatureIcon,
  safe: fundsIcon,
  glove: eventIcon,
  handshake: resultIcon,
  payment: payoutIcon,
};

const formatName = (name: string) =>
  name.replace(/_/g, " ").toLowerCase().replace(/^\w/, (firstLetter) => firstLetter.toUpperCase());

interface DealWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: OpportunityCardTemplate[];
  templatesLoading: boolean;
  onCreated: () => void;
  matchupId?: string | number | null;
  eventDefaults?: EventOpportunityDefaults;
}

const DealWizardModal: React.FC<DealWizardModalProps> = ({
  isOpen,
  onClose,
  templates,
  templatesLoading,
  onCreated,
  matchupId,
  eventDefaults,
}) => {
  const { t: translateText } = useTranslation("marketPlaceCards");

  const [selectedTemplate, setSelectedTemplate] =
    useState<OpportunityCardTemplate | null>(null);

  const [showDetail, setShowDetail] = useState(false);

  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (isOpen && !selectedTemplate && templates.length > 0) {
      setSelectedTemplate(templates[0]);
    }
  }, [isOpen, templates, selectedTemplate]);

  const handleClose = () => {
    setShowForm(false);
    setShowDetail(false);
    setSelectedTemplate(null);
    onClose();
  };

  const handleCreated = () => {
    handleClose();
    onCreated();
  };

  const handleBack = () => {
    if (showForm) {
      setShowForm(false);
    } else if (showDetail) {
      setShowDetail(false);
    } else {
      handleClose();
    }
  };

  const mobileTitle = showForm
    ? selectedTemplate
      ? formatName(selectedTemplate.templateName)
      : translateText("deal_wizard")
    : showDetail
      ? selectedTemplate
        ? formatName(selectedTemplate.templateName)
        : translateText("deal_wizard")
      : translateText("deal_wizard");

  if (!isOpen) return null;

  const templateJson: OpportunityCardTemplateJson | undefined = selectedTemplate?.templateJson;
  const summaryLines = templateJson?.summary ?? [];
  const contractProgressSteps = templateJson?.contractProgress ?? [];
  const visibleSteps = (templateJson?.steps ?? []).filter((step) => step.visible !== false);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:w-[1175px] sm:max-w-[95vw] h-full sm:h-auto sm:max-h-[94dvh] bg-white sm:rounded-3xl shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden">

        <div className="flex items-center gap-3 px-4 sm:px-8 py-4 sm:py-5 border-b border-gray-100 shrink-0">
          <button
            type="button"
            onClick={handleBack}
            className="sm:hidden size-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-[#11181C] leading-6 truncate sm:pr-8">
              <span className="sm:hidden">{mobileTitle}</span>
              <span className="hidden sm:inline">
                {translateText("deal_wizard")}
              </span>
            </h2>
            <p className="hidden sm:block text-xs font-normal leading-5 text-[#71717A] max-w-4xl mt-0.5">
              {translateText("deal_wizard_desc")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="hidden sm:flex size-6 items-center justify-center rounded-xl bg-[#F4F4F5] p-1 text-[#71717A] outline-none transition-colors hover:bg-[#E4E4E7] shrink-0"
            aria-label={translateText("close")}
          >
            <XMarkIcon className="size-4" strokeWidth={2} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 overflow-hidden">

          <div className={`flex flex-col w-full sm:w-64 sm:flex shrink-0 overflow-y-auto border-r border-gray-100 px-4 sm:px-3 py-4 gap-2 ${showDetail || showForm ? "hidden sm:flex" : "flex"}`}>
            <p className="sm:hidden text-xs text-[#71717A] mb-2 leading-5">
              {translateText("deal_wizard_desc")}
            </p>
            {templatesLoading
              ? Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={index}
                  className="h-11 animate-pulse rounded-2xl bg-[#F4F4F5]"
                />
              ))
              : templates.map((templateItem) => {
                const isActive = selectedTemplate?.id === templateItem.id;
                return (
                  <button
                    key={templateItem.id}
                    type="button"
                    onClick={() => {
                      setSelectedTemplate(templateItem);
                      setShowDetail(true);
                    }}
                    className={`flex min-h-11 w-full items-center gap-3 rounded-2xl px-4 py-2 text-left outline-none transition-colors ${
                      isActive
                        ? "bg-[#D9EBFF] text-[#0080FF] font-semibold"
                        : "border border-[#E4E4E7] text-[#11181C] hover:bg-[#FAFAFA]"
                    }`}
                  >
                    <DocumentIcon
                      className={`size-4 shrink-0 ${isActive ? "text-[#0080FF]" : "text-[#71717A]"}`}
                    />
                    <span className="text-sm font-medium truncate flex-1">
                      {formatName(templateItem.templateName)}
                    </span>
                    {isActive && <CheckIcon className="size-4 shrink-0 text-[#0080FF]" />}
                  </button>
                );
              })}
          </div>

          <div className={`flex min-w-0 flex-1 flex-col min-h-0 ${showDetail || showForm ? "flex" : "hidden sm:flex"}`}>
            {showForm && selectedTemplate ? (
              <div className="flex-1 min-h-0 flex flex-col [&>*]:min-h-0">
                <OpportunityFormContainer
                  key={selectedTemplate.id}
                  template={selectedTemplate}
                  matchupId={matchupId}
                  eventDefaults={eventDefaults}
                  onCreated={handleCreated}
                />
              </div>
            ) : (
              <div className="flex flex-col min-h-0">
                <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6 space-y-4">
                  {selectedTemplate ? (
                    <>
                      <h3 className="hidden sm:block text-xl font-bold text-[#11181C]">
                        {formatName(selectedTemplate.templateName)}
                      </h3>

                      {summaryLines.map((summaryLine) => (
                        <p
                          key={summaryLine}
                          className="text-sm text-[#71717A] leading-relaxed"
                        >
                          {summaryLine}
                        </p>
                      ))}

                      <p className="text-sm font-medium text-[#11181C]">
                        {translateText("template_steps_label", { count: visibleSteps.length })}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {visibleSteps.map((step, index) => (
                          <span
                            key={step.stepId}
                            className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#E4E4E7] text-xs font-medium text-[#11181C]"
                          >
                            <span className="w-4 h-4 rounded-full bg-[#D9EBFF] text-[#0080FF] text-[10px] font-bold flex items-center justify-center shrink-0">
                              {index + 1}
                            </span>
                            {step.title}
                          </span>
                        ))}
                      </div>

                      {contractProgressSteps.length > 0 && (
                        <div className="space-y-3 pt-2">
                          <p className="text-sm font-medium text-[#11181C]">
                            {translateText("contract_progress_label")}
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            {contractProgressSteps.map((progressStep, index) => (
                              <React.Fragment key={progressStep.id}>
                                {index > 0 && (
                                  <ChevronRightIcon className="size-4 shrink-0 text-[#71717A]" />
                                )}
                                <div className="flex w-32 flex-col items-center gap-2 rounded-xl border border-[#E4E4E7] p-3 text-center">
                                  <p className="text-xs font-semibold text-[#11181C]">
                                    {progressStep.title}
                                  </p>
                                  <p className="text-[10px] text-[#71717A]">
                                    {progressStep.subtitle}
                                  </p>
                                  <img
                                    src={CONTRACT_PROGRESS_ICONS[progressStep.icon]}
                                    alt=""
                                    className="size-12"
                                  />
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-[#71717A]">
                      {translateText("no_templates_available")}
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center justify-end px-4 sm:px-8 py-3.5 sm:py-4 border-t border-gray-100 bg-white">
                  <Button
                    isDisabled={!selectedTemplate}
                    onPress={() => setShowForm(true)}
                    className="w-full sm:w-auto rounded-full bg-[#0080FF] px-8 py-2.5 text-sm font-bold text-white hover:bg-blue-600 transition shadow-sm h-11 sm:h-auto"
                  >
                    {translateText("start")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealWizardModal;
